import { useEffect, useState } from "react";
import { Delete, Lock } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "del"];

export default function PinLock({ onUnlock }) {
  const [pinSet, setPinSet] = useState(null);
  const [pin, setPin] = useState("");
  const [first, setFirst] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/auth/status").then((r) => setPinSet(r.data.pin_set)); }, []);

  const press = (k) => {
    setErr("");
    if (k === "del") return setPin((p) => p.slice(0, -1));
    if (k && pin.length < 6) setPin((p) => p + k);
  };

  useEffect(() => {
    const h = (e) => {
      if (/^\d$/.test(e.key)) press(e.key);
      if (e.key === "Backspace") press("del");
      if (e.key === "Enter") submit();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });

  const submit = async () => {
    if (pinSet === null) return;
    if (pin.length < 4 || busy) return setErr(pin.length < 4 ? "PIN must be 4–6 digits" : "");
    if (!pinSet && first === null) { setFirst(pin); setPin(""); return; }
    if (!pinSet && first !== pin) { setErr("PINs don't match — start again"); setFirst(null); setPin(""); return; }
    setBusy(true);
    try {
      const r = await api.post(pinSet ? "/auth/login" : "/auth/setup", { pin });
      localStorage.setItem("lvrc_token", r.data.token);
      onUnlock();
    } catch (e) { setErr(errMsg(e)); setPin(""); }
    setBusy(false);
  };

  const title = pinSet ? "Enter PIN" : first === null ? "Create a PIN" : "Confirm your PIN";
  return (
    <div className="min-h-screen grid-bg flex items-center justify-center p-6" data-testid="pin-lock-screen">
      <div className="panel w-full max-w-xs p-6 rise">
        <div className="flex items-center gap-2 mb-1"><Lock size={16} className="text-cyan-400" /><span className="label-xs">LVRC Terminal</span></div>
        <h1 className="text-2xl font-semibold text-slate-100 mb-1" data-testid="pin-title">{pinSet === null ? "…" : title}</h1>
        <p className="text-xs text-slate-400 mb-5">{pinSet ? "Unlock to access live trading and settings." : "4–6 digits. Protects live trading."}</p>
        <div className="flex justify-center gap-3 mb-4" data-testid="pin-dots">
          {Array.from({ length: Math.max(4, pin.length) }).map((_, i) => (
            <span key={i} className={`h-3 w-3 rounded-full border ${i < pin.length ? "bg-cyan-400 border-cyan-400" : "border-slate-600"}`} />
          ))}
        </div>
        <div className="h-5 text-center text-xs text-amber-400 mb-2" data-testid="pin-error">{err}</div>
        <div className="grid grid-cols-3 gap-2">
          {KEYS.map((k, i) => k === "" ? <span key={i} /> : (
            <button key={i} data-testid={`pin-key-${k}`} onClick={() => press(k)}
              className="h-14 rounded-md bg-[#1A2235] hover:bg-[#233049] active:scale-95 transition-transform font-mono text-xl text-slate-100 flex items-center justify-center">
              {k === "del" ? <Delete size={18} /> : k}
            </button>
          ))}
        </div>
        <button data-testid="pin-submit-btn" onClick={submit} disabled={busy || pinSet === null}
          className="mt-4 w-full h-12 rounded-md bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold transition-colors disabled:opacity-50">
          {pinSet ? "Unlock" : first === null ? "Next" : "Set PIN"}
        </button>
      </div>
    </div>
  );
}
